import { create } from "zustand";
import type { Comparison } from "@/types/paper";

interface ConceptBucket {
  selectedPaperIds: Set<string>;
  selectedPresetColumns: Set<string>;
  customColumns: string[];
}

interface State {
  byConcept: Record<string, ConceptBucket>;
  pendingInsertConcept: string | null;
  pendingInsertComparison: Comparison | null;
  togglePaper: (conceptId: string, paperId: string) => void;
  toggleColumn: (conceptId: string, column: string) => void;
  addCustomColumn: (conceptId: string, column: string) => void;
  removeCustomColumn: (conceptId: string, column: string) => void;
  clearForConcept: (conceptId: string) => void;
  requestInsert: (conceptId: string, comparison: Comparison) => void;
  consumePendingInsert: () => Comparison | null;
}

function emptyBucket(): ConceptBucket {
  return {
    selectedPaperIds: new Set(),
    selectedPresetColumns: new Set(),
    customColumns: [],
  };
}

export const useComparisonStore = create<State>((set, get) => ({
  byConcept: {},
  pendingInsertConcept: null,
  pendingInsertComparison: null,

  togglePaper: (conceptId, paperId) =>
    set((state) => {
      const prev = state.byConcept[conceptId] ?? emptyBucket();
      const next = new Set(prev.selectedPaperIds);
      if (next.has(paperId)) next.delete(paperId);
      else next.add(paperId);
      return {
        byConcept: {
          ...state.byConcept,
          [conceptId]: { ...prev, selectedPaperIds: next },
        },
      };
    }),

  toggleColumn: (conceptId, column) =>
    set((state) => {
      const prev = state.byConcept[conceptId] ?? emptyBucket();
      const next = new Set(prev.selectedPresetColumns);
      if (next.has(column)) next.delete(column);
      else next.add(column);
      return {
        byConcept: {
          ...state.byConcept,
          [conceptId]: { ...prev, selectedPresetColumns: next },
        },
      };
    }),

  addCustomColumn: (conceptId, column) =>
    set((state) => {
      const trimmed = column.trim();
      if (!trimmed) return state;
      const prev = state.byConcept[conceptId] ?? emptyBucket();
      const lower = trimmed.toLowerCase();
      if (prev.customColumns.some((c) => c.toLowerCase() === lower)) return state;
      return {
        byConcept: {
          ...state.byConcept,
          [conceptId]: { ...prev, customColumns: [...prev.customColumns, trimmed] },
        },
      };
    }),

  removeCustomColumn: (conceptId, column) =>
    set((state) => {
      const prev = state.byConcept[conceptId];
      if (!prev) return state;
      const lower = column.toLowerCase();
      return {
        byConcept: {
          ...state.byConcept,
          [conceptId]: {
            ...prev,
            customColumns: prev.customColumns.filter((c) => c.toLowerCase() !== lower),
          },
        },
      };
    }),

  clearForConcept: (conceptId) =>
    set((state) => {
      if (!(conceptId in state.byConcept)) return state;
      const next = { ...state.byConcept };
      delete next[conceptId];
      return { byConcept: next };
    }),

  requestInsert: (conceptId, comparison) =>
    set({
      pendingInsertConcept: conceptId,
      pendingInsertComparison: comparison,
    }),

  consumePendingInsert: () => {
    const c = get().pendingInsertComparison;
    set({ pendingInsertConcept: null, pendingInsertComparison: null });
    return c;
  },
}));
